import React from 'react';
import {
  CreditCard as CreditCardComponent,
  Box,
  CreditCardType,
} from '@chargify/custom-ui-components';

import CardNumber from '../CardNumber';
import CardExpiration from '../CardExpiration';
import SecurityCode from '../SecurityCode';
import { TooltipContent } from './components';

import { Container, Row, Column } from './CreditCard.styles';

import { FieldsLabels, CardSize } from './types';
import { CreditCardPCIFields } from '../../types';

type CreditCardProps = {
  /**
   * Credit card required fields
   */
  requiredFields?: string[];
  /**
   * Card fields labels
   */
  labels: FieldsLabels;
  /**
   * Card size
   */
  size?: CardSize;
  /**
   * Card fields errors
   */
  errors?: string[];
  /**
   * Card type
   */
  type?: CreditCardType;
};

const CreditCard = ({
  requiredFields = [],
  errors = [],
  labels,
  size = 'regular',
  type,
}: CreditCardProps) => {
  const {
    cardNumber,
    securityCode,
    expirationDate,
    threeDigitTooltip,
    fourDigitTooltip,
  } = labels;

  return (
    <Container
      size={size}
      borderWidth="1px"
      borderStyle="solid"
      borderColor="gray.light"
      borderRadius={3}
    >
      <Box marginBottom={3}>
        <CreditCardComponent.Chip />
      </Box>
      <Row
        gridColumnGap={4}
        gridRowGap={3}
        gridTemplateColumns="repeat(8, 1fr)"
      >
        <Column gridRow={1} gridColumn={size === 'small' ? 'span 6' : 'span 5'}>
          <CardNumber
            label={cardNumber}
            hasError={errors.includes(CreditCardPCIFields.Number)}
            required
          />
        </Column>
        <Column
          gridRow={size === 'small' ? 2 : 1}
          gridColumn={size === 'small' ? 'span 4' : 'span 3'}
        >
          <CardExpiration
            label={expirationDate}
            hasError={
              errors.includes(CreditCardPCIFields.ExpirationMonth) ||
              errors.includes(CreditCardPCIFields.ExpirationYear) ||
              errors.includes('expiration')
            }
            required
          />
        </Column>
        <Column gridRow="2" gridColumn="span 4">
          <SecurityCode
            label={securityCode}
            hasError={errors.includes(CreditCardPCIFields.SecurityCode)}
            required={requiredFields.includes(CreditCardPCIFields.SecurityCode)}
            tooltip={{
              renderContent: () => (
                <TooltipContent
                  labels={{
                    threeDigitTooltip,
                    fourDigitTooltip,
                  }}
                />
              ),
              mode: 'dark',
            }}
          />
        </Column>
        <Column
          gridColumn={size === 'small' ? '7/-1' : '6/-1'}
          gridRow={size === 'small' ? 1 : 2}
          placeSelf="flex-end"
        >
          <CreditCardComponent.Logo type={type} />
        </Column>
      </Row>
      <CreditCardComponent.Watermark type={type} size={size} />
    </Container>
  );
};

export default CreditCard;
